// src/Pages/EmployeeManagement.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api.js';
import EmployeeForm from '../Components/EmployeeForm.js';
import EmployeeList from '../Components/EmployeeList.js';
import PaginationControls from '../Components/PaginationControls.js';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import styles from './EmployeeManagement.module.css';

const INITIAL_FORM_STATE = {
  id: null,
  name: '',
  last_name: '',
  mail: '',
  oldPassword: '',
  password: '',
  tel_number: '',
  identification: '',
  birthdate: '',
  position: ''
};

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // --- Data Fetching: Empleados (usuarios con rol 'employee') ---
  const fetchEmployees = useCallback(async (currentPage) => {
    setIsLoading(true);
    try {
      const res = await api.get('/user', {
        params: { page: currentPage, limit: 10 }
      });


      // Filtrar solo empleados
      const allUsers = res.data.users || [];
      const employeesOnly = allUsers.filter(u => (u.Rol || u.role) === 'employee');

      setEmployees(employeesOnly);
      setPage(res.data.page);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      showToast('Error al cargar empleados. Verifica la consola.', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEmployees(page);
  }, [page, fetchEmployees]);


  // --- Mutaciones ---

  const handleUpdateEmployeeList = (updatedEmployee) => {
    setEmployees(prevEmps => {
      if (updatedEmployee.id) {
        // Edición
        return prevEmps.map(emp =>
          (emp.ID || emp.id) === updatedEmployee.id ? { ...emp, ...updatedEmployee } : emp
        );
      }
      // Creación
      return [updatedEmployee, ...prevEmps].slice(0, 10);
    });
  };

  const handleDelete = useCallback(async (id) => {
    const ok = await confirm('¿Desactivar este empleado? Esta acción es reversible.', {
      title: 'Confirmar desactivación',
      confirmText: 'Desactivar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/user/${id}`);

      // Actualización local
      setEmployees(prevEmps => prevEmps.filter(emp => (emp.ID || emp.id) !== id));
      if (form.id === id) setForm(INITIAL_FORM_STATE);

      // Recarga
      fetchEmployees(page);
      showToast('Empleado desactivado correctamente.', { type: 'success' });
    } catch (err) {
      showToast('Error al desactivar empleado.', { type: 'error' });
    }
  }, [form.id, fetchEmployees, page, confirm, showToast]);

  const formRef = useRef(null);

  const handleEdit = useCallback(async (emp) => {
    const empId = emp.ID || emp.id;
    try {
      const res = await api.get(`/user/${empId}`);
      const u = res.data;
      setForm({
        id:             empId,
        name:           u.Name           || u.name           || '',
        last_name:      u.Last_name      || u.last_name      || '',
        mail:           u.Email          || u.mail           || '',
        oldPassword:    '',
        password:       '',
        tel_number:     u.Number_phone   || u.tel_number     || '',
        identification: u.Identification || u.identification || '',
        birthdate:      u.Birthdate      ? u.Birthdate.split('T')[0]
                      : u.birthdate      ? u.birthdate.split('T')[0]
                      : '',
        position:       u.Position       || u.position       || '',
      });
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (err) {
      showToast('No se pudo cargar la información completa del empleado.', { type: 'error' });
    }
  }, [showToast]);

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM_STATE);
  }, []);

  // --- Renderizado ---

  return (
    <div className={styles.container}>
      <h2>Gestión de Empleados</h2>

      {/* 1. Formulario */}
      <div ref={formRef}>
        <EmployeeForm
          form={form}
          setForm={setForm}
          resetForm={resetForm}
          onSuccess={handleUpdateEmployeeList}
        />
      </div>

      <h3 className={styles.listTitle}>Lista de Empleados</h3>

      {/* 2. Lista/Tabla */}
      <div style={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.15s', pointerEvents: isLoading ? 'none' : 'auto' }}>
        <EmployeeList
          employees={employees}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      {/* 3. Paginación */}
      {totalPages > 1 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;
